import { useNavigate } from 'react-router-dom';
import { useForm } from "react-hook-form";
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import ErrorPopup from '@/components/custom/InfoPopups/ErrorPopup';
import SuccessPopup from '@/components/custom/InfoPopups/SuccessPopup';
import InputFloating from '@/components/atoms/InputFloating';
import { Button } from '@/components/ui/button';
import PageWrapper from '@/components/atoms/PageWrapper';
import LoaderWithBackdrop from '@/components/custom/LoaderWithBackdrop';
import { useForgotPassword } from '@/features/user/hooks/useMutation/useForgotPassword';


export default function RequestPasswordReset() {
    const navigate = useNavigate();
    const { mutateAsync: forgotPassword, isPending } = useForgotPassword();

    const schema = z.object({
        email: z.string().email("Email is not valid").nonempty({ message: "Name is required" })
    });

    type inputFields = z.infer<typeof schema>;
    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm<inputFields>({ resolver: zodResolver(schema), mode: "all" });

    const goBackHandler = () => {
        navigate('/login');
    }

    const onSubmit = async (formData: { email: string }) => {
        if (isPending) return;

        try {
            const response = await forgotPassword(formData.email);

            if (!response.error) {
                SuccessPopup("Success! Password reset link was sent to your email address.");
                navigate('/login');
            } else {
                console.log(response.error);
                ErrorPopup(response.error.message);
            }
        } catch (err) {
            ErrorPopup("An error occurred. Please check your internet connection");
        }
    };

    return (
        <PageWrapper className='flex flex-col items-center flex-1 w-full max-w-full custom_bg'>
            {isPending && <LoaderWithBackdrop />}
            <div className="justify-center max-w-xl px-3 py-8 rounded sm:px-8 bg-gray-50/80">
                <div className="mb-4">
                    <h1 className="font-bold text-center">Reset Password</h1>
                </div>

                <div className="p-0 m-0 mb-3">
                    <p className="p-0 m-0 text-center">Please enter your email address. You will receive a link to create a new password via email.</p>
                </div>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="space-y-2">
                        <InputFloating
                            label="Email Address"
                            type="email"
                            errorMessage={errors?.email?.message?.toString()}
                            showError={true}
                            register={register("email")}
                        />
                    </div>

                    <div className="flex flex-wrap justify-between gap-2 mt-4">
                        <div className="w-full sm:w-auto">
                            <Button type="button" variant="secondary" className="w-full" title="Back" onClick={goBackHandler}>Back</Button>
                        </div>

                        <div className="w-full sm:w-auto">
                            <Button type="submit" className="w-full">Reset Password</Button>
                        </div>
                    </div>
                </form>
            </div>
        </PageWrapper>
    )
}
